import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import ScrollableTableContainer from '../common/ScrollableTableContainer';
import { buildApiUrl } from '../../utils/apiSetup';

const calculateRowTotals = (emps, months) => {
  const totals = {
    opening_bal: 0,
    monthly: {},
    balance: 0,
    interest: 0,
    closing_balance: 0,
  };

  (months || []).forEach((m) => {
    totals.monthly[m.key] = {
      pfs: 0,
      vpf: 0,
      pfadv: 0,
      ref_loan: 0,
      non_ref_loan: 0,
    };
  });

  (emps || []).forEach((emp) => {
    totals.opening_bal += Math.round(Number(emp.opening_bal) || 0);
    totals.balance += Math.round(Number(emp.balance) || 0);
    totals.interest += Math.round(Number(emp.interest) || 0);
    totals.closing_balance += Math.round(Number(emp.closing_balance) || 0);

    (months || []).forEach((m) => {
      const val = emp.monthly_data?.[m.key] || {};
      totals.monthly[m.key].pfs += Math.round(Number(val.pfs) || 0);
      totals.monthly[m.key].vpf += Math.round(Number(val.vpf) || 0);
      totals.monthly[m.key].pfadv += Math.round(Number(val.pfadv) || 0);
      totals.monthly[m.key].ref_loan += Math.round(Number(val.ref_loan) || 0);
      totals.monthly[m.key].non_ref_loan += Math.round(Number(val.non_ref_loan) || 0);
    });
  });

  return totals;
};

const PCBLedgerView = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ledgerData, setLedgerData] = useState(null);

  // Generate Financial Year options from 2025-26 up to the current financial year
  const financialYearsList = useMemo(() => {
    const list = [];
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const maxStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;

    for (let y = maxStartYear; y >= 2025; y--) {
      const nextYearShort = String(y + 1).slice(-2);
      list.push(`${y}-${nextYearShort}`);
    }
    // Fallback if current year is somehow before 2025 to keep at least 2025-26 in the list
    if (list.length === 0) {
      list.push("2025-26");
    }
    return list;
  }, []);

  // Determine default FY based on current date (if after April, it's current_year - next_year_short)
  const defaultFY = useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-indexed
    const currentYear = today.getFullYear();
    if (currentMonth >= 4) {
      return `${currentYear}-${String(currentYear + 1).slice(-2)}`;
    } else {
      return `${currentYear - 1}-${String(currentYear).slice(-2)}`;
    }
  }, []);

  const [financialYear, setFinancialYear] = useState(defaultFY);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [interestCalc, setInterestCalc] = useState(null);
  const [interestRateInput, setInterestRateInput] = useState('7.10');
  const [processingInterest, setProcessingInterest] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loadLedgerData = async () => {
    setLoading(true);
    setError('');
    try {
      const deptParam = selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : '';
      const response = await fetch(buildApiUrl(`/api/v1/reports/pf-ledger?financial_year=${financialYear}${deptParam}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to load PF ledger report data');
      }
      const data = await response.json();
      setLedgerData(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading ledger data');
    } finally {
      setLoading(false);
    }
  };

  const loadInterestCalc = async () => {
    try {
      const res = await fetch(buildApiUrl(`/api/v1/pf-interest/calculation?fy=${financialYear}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInterestCalc(data);
        if (data && data.final_interest_rate) {
          setInterestRateInput(String(data.final_interest_rate));
        }
      }
    } catch (err) {
      console.error('Error fetching interest calc:', err);
    }
  };

  useEffect(() => {
    loadLedgerData();
    loadInterestCalc();
  }, [financialYear, selectedDepartment]);

  const handleProcessInterestCredit = async () => {
    if (!interestCalc || interestCalc.status !== 'finalized') {
      setError(`Interest rate for Financial Year ${financialYear} has not been finalized in the PF module yet.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to credit interest @ ${interestCalc.final_interest_rate}% to all employee PF accounts for Financial Year ${financialYear}?`)) {
      return;
    }

    setProcessingInterest(true);
    setError('');
    setSuccessMsg('');

    try {
      const creditRes = await fetch(buildApiUrl(`/api/v1/pf-interest/process-credit?fy=${financialYear}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (!creditRes.ok) {
        const creditErr = await creditRes.json();
        throw new Error(creditErr.detail || 'Failed to process interest credit');
      }

      const creditResult = await creditRes.json();
      setSuccessMsg(`PF Interest @ ${interestCalc.final_interest_rate}% credited to ${creditResult.processed_count || 'all'} employee accounts! (${creditResult.skipped_count || 0} already credited)`);
      await loadLedgerData();
      await loadInterestCalc();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error processing interest credit');
    } finally {
      setProcessingInterest(false);
    }
  };

  const reportMonths = useMemo(() => {
    return ledgerData?.months || [];
  }, [ledgerData]);

  const departments = useMemo(() => {
    return ledgerData?.departments || {};
  }, [ledgerData]);

  const allDepartmentsList = useMemo(() => {
    if (ledgerData?.all_departments && Array.isArray(ledgerData.all_departments)) {
      return ledgerData.all_departments;
    }
    return Object.keys(ledgerData?.departments || {});
  }, [ledgerData]);

  // Default to first department when list loads
  useEffect(() => {
    if (allDepartmentsList.length > 0) {
      if (!selectedDepartment || !allDepartmentsList.includes(selectedDepartment)) {
        setSelectedDepartment(allDepartmentsList[0]);
      }
    }
  }, [allDepartmentsList, selectedDepartment]);

  // Download PDF handler for PF Ledger
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const deptParam = selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : '';
      const url = `/api/v1/reports/pf-ledger-pdf?financial_year=${financialYear}${deptParam}`;
      await downloadAuthenticatedFile(
        url,
        `PF_Ledger_FY_${financialYear}.pdf`
      );
    } catch (err) {
      console.error('Error downloading PF Ledger PDF:', err);
      // Fallback: use window print
      window.print();
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Filters & Interest Finalization Control Bar */}
      <div className="print:hidden bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 w-full">
          <div className="flex flex-wrap items-center gap-4">
            <Calendar className="w-5 h-5 text-blue-600 self-center" />

            {/* Financial Year Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">FINANCIAL YEAR</label>
              <select
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[130px]"
              >
                {financialYearsList.map((fy) => (
                  <option key={fy} value={fy}>
                    {fy}
                  </option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">DEPARTMENT</label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[200px]"
              >
                {allDepartmentsList.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons & PF Interest Status */}
          <div className="flex items-center gap-3">
            {hasPermission('pf:add') && interestCalc?.status === 'finalized' && (
              <button
                onClick={handleProcessInterestCredit}
                disabled={processingInterest || loading}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
              >
                {processingInterest && <Loader2 className="w-4 h-4 animate-spin" />}
                Credit PF Interest ({interestCalc.final_interest_rate}%)
              </button>
            )}

            {hasPermission('report:export') && (
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF
              </button>
            )}
          </div>
        </div>

        {interestCalc?.status === 'finalized' ? (
          <div className="px-3 py-2 bg-green-50 border border-green-200 text-green-800 rounded-lg text-xs font-semibold flex items-center justify-between">
            <span>
              FY {financialYear} Finalized PF Interest Rate: <strong>{interestCalc.final_interest_rate}%</strong> (Finalized in PF Module)
            </span>
          </div>
        ) : (
          <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-xs font-semibold">
            <span>
              FY {financialYear} Interest Rate Status: <strong>Draft / Not Finalized</strong> (Finalize rate in Provident Fund &gt; PF Interest Calculation)
            </span>
          </div>
        )}

        {successMsg && (
          <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center gap-2">
            <span>{successMsg}</span>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Ledger Report Display Table */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
            <p className="text-gray-500 font-medium">Loading PF Ledger report data...</p>
          </div>
        ) : Object.keys(departments).length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-1">No Ledger data found</p>
            <p className="text-sm text-gray-400">Please verify selection ranges above.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* <div className="text-center py-2 border-b border-gray-100">
              <h1 className="text-xl font-bold text-gray-900 tracking-wider">PROVIDENT FUND LEDGER</h1>
            </div> */}

            {/* Scrollable Container with improved UX */}
            <ScrollableTableContainer
              maxHeight={600}
              showTopScrollbar={true}
              stickyHeader={true}
            >
              <table className="min-w-max text-sm font-mono border-collapse bg-white text-gray-900">
                <thead>
                  {/* Row 1 Headers */}
                  <tr className="bg-gray-100 border-b border-gray-900 text-gray-900 uppercase font-bold text-xs leading-normal">
                    <th rowSpan="2" className="sticky left-0 bg-gray-100 border-r border-gray-900 px-3 py-3 text-center min-w-[60px] z-30">Sr. No.</th>
                    <th rowSpan="2" className="sticky left-[60px] bg-gray-100 border-r border-gray-900 px-3 py-3 text-center min-w-[85px] z-30">code no.</th>
                    <th rowSpan="2" className="sticky left-[145px] bg-gray-100 border-r border-gray-900 px-4 py-3 text-left min-w-[230px] z-30">NAME</th>
                    <th rowSpan="2" className="sticky left-[375px] bg-gray-100 border-r border-gray-900 px-3 py-3 text-left min-w-[150px] z-30">designation</th>
                    <th rowSpan="2" className="sticky left-[525px] bg-gray-100 border-r border-gray-900 px-3 py-3 text-right min-w-[115px] z-30">Opening Bal</th>
                    
                    {reportMonths.map((m) => (
                      <th key={m.key} colSpan="6" className="border-r border-gray-900 px-3 py-2 text-center min-w-[400px] font-bold text-sm bg-purple-50/60">
                        {m.label}
                      </th>
                    ))}
                    
                    <th rowSpan="2" className="border-r border-gray-900 px-4 py-3 text-right min-w-[120px] font-bold text-xs bg-emerald-100/70 text-emerald-950">
                      Balance
                    </th>
                    <th rowSpan="2" className="border-r border-gray-900 px-4 py-3 text-right min-w-[100px] font-bold text-xs bg-purple-100/70 text-purple-950">
                      Interest
                    </th>
                    <th rowSpan="2" className="border-r border-gray-900 px-4 py-3 text-right min-w-[130px] font-bold text-xs bg-blue-100/70 text-blue-950">
                      Closing Balance
                    </th>
                  </tr>
                  {/* Row 2 Sub-Headers */}
                  <tr className="bg-gray-50 border-b border-gray-900 text-gray-800 uppercase font-bold text-xs leading-normal">
                    {reportMonths.map((m) => (
                      <React.Fragment key={`sub-${m.key}`}>
                        <th className="border-r border-gray-900 px-2 py-2 text-right min-w-[80px]">pfs</th>
                        <th className="border-r border-gray-900 px-2 py-2 text-right min-w-[80px]">vpf</th>
                        <th className="border-r border-gray-900 px-2 py-2 text-right min-w-[80px]">pfadv</th>
                        <th className="border-r border-gray-900 px-2 py-2 text-right min-w-[100px]">Refundable Loan</th>
                        <th className="border-r border-gray-900 px-2 py-2 text-right min-w-[100px]">Non-Refundable Loan</th>
                        <th className="border-r border-gray-900 px-2 py-2 text-center min-w-[80px]">adv date</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800 text-sm">
                  {Object.entries(departments).map(([deptName, emps]) => (
                    <React.Fragment key={deptName}>
                      {/* Department Heading Row */}
                      <tr className="bg-purple-100/60 font-bold border-y border-gray-900">
                        <td colSpan={5 + reportMonths.length * 6 + 3} className="sticky left-0 px-4 py-2.5 text-left text-gray-900 tracking-wider font-extrabold text-sm z-10">
                          {deptName}
                        </td>
                      </tr>
                      {/* Employee Rows */}
                      {emps.length === 0 ? (
                        <tr className="hover:bg-purple-50/30 text-gray-900 font-medium">
                          <td className="sticky left-0 bg-white border-r border-gray-800 px-3 py-2.5 text-center z-10">-</td>
                          <td className="sticky left-[60px] bg-white border-r border-gray-800 px-3 py-2.5 text-center font-bold text-sm z-10">-</td>
                          <td className="sticky left-[145px] bg-white border-r border-gray-800 px-3 py-2.5 text-left font-bold text-sm z-10 truncate max-w-[230px]">-</td>
                          <td className="sticky left-[375px] bg-white border-r border-gray-800 px-3 py-2.5 text-left text-sm z-10 truncate max-w-[150px]">-</td>
                          <td className="sticky left-[525px] bg-white border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm z-10">0</td>
                          
                          {/* Monthly PF Columns */}
                          {reportMonths.map((m) => (
                            <React.Fragment key={`empty-${m.key}`}>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">0</td>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">0</td>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">0</td>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">0</td>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">0</td>
                              <td className="border-r border-gray-800 px-3 py-2.5 text-center text-sm font-semibold">-</td>
                            </React.Fragment>
                          ))}
                          <td className="border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm bg-emerald-50/40 text-emerald-900">0</td>
                          <td className="border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm bg-purple-50/40 text-purple-900">0</td>
                          <td className="border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm bg-blue-50/40 text-blue-900">0</td>
                        </tr>
                      ) : (
                        emps.map((emp, index) => (
                          <tr key={emp.emp_code} className="hover:bg-purple-50/30 text-gray-900 font-medium">
                            <td className="sticky left-0 bg-white border-r border-gray-800 px-3 py-2.5 text-center z-10">{index + 1}</td>
                            <td className="sticky left-[60px] bg-white border-r border-gray-800 px-3 py-2.5 text-center font-bold text-sm z-10">{emp.emp_code}</td>
                            <td className="sticky left-[145px] bg-white border-r border-gray-800 px-3 py-2.5 text-left font-bold text-sm z-10 truncate max-w-[230px]" title={emp.emp_name}>
                              {emp.emp_name}
                            </td>
                            <td className="sticky left-[375px] bg-white border-r border-gray-800 px-3 py-2.5 text-left text-sm z-10 truncate max-w-[150px]" title={emp.designation}>
                              {emp.designation}
                            </td>
                            <td className="sticky left-[525px] bg-white border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm z-10">{emp.opening_bal || '0'}</td>
                            
                            {/* Monthly PF Columns */}
                            {reportMonths.map((m) => {
                              const val = emp.monthly_data[m.key] || {};
                              return (
                                <React.Fragment key={`${emp.emp_code}-${m.key}`}>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">{val.pfs || ''}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">{val.vpf || ''}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold">{val.pfadv || ''}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold text-amber-700">{val.ref_loan || ''}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-semibold text-rose-700">{val.non_ref_loan || ''}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-center text-sm font-semibold">{val.adv_date || ''}</td>
                                </React.Fragment>
                              );
                            })}
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm bg-emerald-50/40 text-emerald-950">
                              {emp.balance !== undefined ? emp.balance : '-'}
                            </td>
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-bold text-sm bg-purple-50/40 text-purple-950">
                              {emp.interest !== undefined ? emp.interest : '0'}
                            </td>
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-black text-sm bg-blue-50/50 text-blue-950">
                              {emp.closing_balance !== undefined ? emp.closing_balance : '-'}
                            </td>
                          </tr>
                        ))
                      )}

                      {/* Department Total Row */}
                      {emps.length > 0 && (() => {
                        const deptTotals = calculateRowTotals(emps, reportMonths);
                        return (
                          <tr className="bg-gray-100 font-bold border-y-2 border-gray-900 text-gray-900">
                            <td className="sticky left-0 bg-gray-100 border-r border-gray-800 px-3 py-2.5 text-center font-bold text-xs uppercase z-10"></td>
                            <td className="sticky left-[60px] bg-gray-100 border-r border-gray-800 px-3 py-2.5 text-center font-bold text-xs uppercase z-10"></td>
                            <td className="sticky left-[145px] bg-gray-100 border-r border-gray-800 px-3 py-2.5 text-left font-black text-sm z-10 uppercase tracking-wide truncate max-w-[230px]" title={`TOTAL - ${deptName}`}>
                              TOTAL
                            </td>
                            <td className="sticky left-[375px] bg-gray-100 border-r border-gray-800 px-3 py-2.5 text-left font-bold text-xs z-10"></td>
                            <td className="sticky left-[525px] bg-gray-100 border-r border-gray-800 px-3 py-2.5 text-right font-black text-sm z-10">
                              {deptTotals.opening_bal}
                            </td>

                            {/* Monthly PF Columns Totals */}
                            {reportMonths.map((m) => {
                              const val = deptTotals.monthly[m.key] || {};
                              return (
                                <React.Fragment key={`tot-${deptName}-${m.key}`}>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-bold bg-gray-50">{val.pfs || 0}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-bold bg-gray-50">{val.vpf || 0}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-bold bg-gray-50">{val.pfadv || 0}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-bold text-amber-800 bg-gray-50">{val.ref_loan || 0}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-right text-sm font-bold text-rose-800 bg-gray-50">{val.non_ref_loan || 0}</td>
                                  <td className="border-r border-gray-800 px-3 py-2.5 text-center text-sm font-bold text-gray-400 bg-gray-50">-</td>
                                </React.Fragment>
                              );
                            })}
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-black text-sm bg-emerald-100/70 text-emerald-950">
                              {deptTotals.balance}
                            </td>
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-black text-sm bg-purple-100/70 text-purple-950">
                              {deptTotals.interest}
                            </td>
                            <td className="border-r border-gray-800 px-3 py-2.5 text-right font-black text-sm bg-blue-100/70 text-blue-950">
                              {deptTotals.closing_balance}
                            </td>
                          </tr>
                        );
                      })()}
                    </React.Fragment>
                  ))}

                  {/* Grand Total Row if multiple departments are shown */}
                  {Object.keys(departments).length > 1 && (() => {
                    const allEmps = Object.values(departments).flat();
                    if (allEmps.length === 0) return null;
                    const grandTotals = calculateRowTotals(allEmps, reportMonths);
                    return (
                      <tr className="bg-gray-200 font-bold border-t-2 border-b-4 border-gray-950 text-gray-950">
                        <td className="sticky left-0 bg-gray-200 border-r border-gray-900 px-3 py-3 text-center font-bold text-xs uppercase z-10"></td>
                        <td className="sticky left-[60px] bg-gray-200 border-r border-gray-900 px-3 py-3 text-center font-bold text-xs uppercase z-10"></td>
                        <td className="sticky left-[145px] bg-gray-200 border-r border-gray-900 px-3 py-3 text-left font-black text-sm z-10 uppercase tracking-wider truncate max-w-[230px]" title="GRAND TOTAL">
                          GRAND TOTAL
                        </td>
                        <td className="sticky left-[375px] bg-gray-200 border-r border-gray-900 px-3 py-3 text-left font-bold text-xs z-10"></td>
                        <td className="sticky left-[525px] bg-gray-200 border-r border-gray-900 px-3 py-3 text-right font-black text-sm z-10">
                          {grandTotals.opening_bal}
                        </td>

                        {reportMonths.map((m) => {
                          const val = grandTotals.monthly[m.key] || {};
                          return (
                            <React.Fragment key={`grand-${m.key}`}>
                              <td className="border-r border-gray-900 px-3 py-3 text-right text-sm font-black bg-gray-100">{val.pfs || 0}</td>
                              <td className="border-r border-gray-900 px-3 py-3 text-right text-sm font-black bg-gray-100">{val.vpf || 0}</td>
                              <td className="border-r border-gray-900 px-3 py-3 text-right text-sm font-black bg-gray-100">{val.pfadv || 0}</td>
                              <td className="border-r border-gray-900 px-3 py-3 text-right text-sm font-black text-amber-900 bg-gray-100">{val.ref_loan || 0}</td>
                              <td className="border-r border-gray-900 px-3 py-3 text-right text-sm font-black text-rose-900 bg-gray-100">{val.non_ref_loan || 0}</td>
                              <td className="border-r border-gray-900 px-3 py-3 text-center text-sm font-bold text-gray-500 bg-gray-100">-</td>
                            </React.Fragment>
                          );
                        })}

                        <td className="border-r border-gray-900 px-3 py-3 text-right font-black text-sm bg-emerald-200 text-emerald-950">
                          {grandTotals.balance}
                        </td>
                        <td className="border-r border-gray-900 px-3 py-3 text-right font-black text-sm bg-purple-200 text-purple-950">
                          {grandTotals.interest}
                        </td>
                        <td className="border-r border-gray-900 px-3 py-3 text-right font-black text-sm bg-blue-200 text-blue-950">
                          {grandTotals.closing_balance}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </ScrollableTableContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default PCBLedgerView;
